import { add, addDays, addMonths, endOfMonth, format, parse, startOfMonth } from 'date-fns';
import { BookingDetails, CountryOption, PriceOptions, Translations } from '../redux/types';
import { Color, DateRangeStr, MileageOption, Price, PriceCalculation } from '../types/types';
import { countriesList, trans } from './trans';
import { formatPrice, t } from '../redux/utils';
import { ShadowType, RoundingType, CartButtonShape, SettingsFontType, CartPosition, WidgetConfig } from '../types/config';
import { Locale } from 'date-fns/locale/types';

export const detectCheckout = (): string[] => {
  const idsOfCheckouts = [];

  const findChildrenWithShadow = (children: HTMLCollection) => {
    let childrenValues = Object.values(children);

    childrenValues.forEach((child: HTMLElement) => {
      if (child.tagName === 'RTR-CHECKOUT') {
        idsOfCheckouts.push(child.getAttribute('id'));
      } else {
        // do not look inside checkout for other checkouts
        // (checkouts children still could not be rendered anyway)
        if (child.children.length) {
          findChildrenWithShadow(child.children);
        }

        if (child.shadowRoot?.children && child.shadowRoot.children.length) {
          findChildrenWithShadow(child.shadowRoot.children);
        }
      }
    });
  };

  findChildrenWithShadow(document.body.children);

  return idsOfCheckouts;
};

export function centsToFixed2(cents) {
  if (!cents && cents !== 0) {
    return '';
  }
  return (cents / 100).toFixed(2);
}

export function triggerInputChange(element: HTMLInputElement, value: string) {
  element.value = value;
  const event = new Event('input', { bubbles: true });
  element.dispatchEvent(event);
}

// ↓ use with UI
export function dateToLocalizedString(d: Date): string {
  if (!d) return '';

  return format(d, 'P');
}

export function dateToLocalizedStringShort(d: Date): string {
  if (!d) return '';

  const formatted = format(d, 'P');
  const trimmed = formatted.slice(0, -5); // for all used locales year is on last place, maybe not true with new languages

  return trimmed;
}

export function dateToLocalizedStringHour(d: Date): string {
  if (!d) return '';

  return format(d, 'p');
}

export function hourToLocalizedStringHour(d: string): string {
  if (!d) return '';

  return format(parse(d, 'HH:mm', new Date()), 'p');
}

export function formatDateTimeAndLocalize(date: Date, time: string, secondTime: string, withTime: boolean) {
  let formatted = dateToLocalizedString(date);

  if (date && withTime) {
    formatted += `, ${(time && hourToLocalizedStringHour(time)) || '?'}`;
    if (secondTime) {
      formatted += ` - ${hourToLocalizedStringHour(secondTime)}`;
    }
  }

  return formatted;
}
// ↑ use with UI

// ↓ use only with API and time state
export function dateToString(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

export function dateToHourString(d: Date): string {
  return format(d, 'HH:mm');
}
// ↑ use only with API

export function getQueryParam(name: string): string {
  const queryParams = new URLSearchParams(window.location.search);

  return queryParams.get(name);
}

export function createSlotDivOnDocumentLevel(host: Element, slotName: string, id: string, width = ''): void {
  // trace each shadow boundary between us and the document
  const shadowHosts = [host];
  while ((host = (host.getRootNode() as ShadowRoot).host)) {
    shadowHosts.push(host);
  }

  // Prepare the shallowest breadcrumb slot at document level
  const hosts = [...shadowHosts];
  const root = hosts.pop();
  if (!root.querySelector(`[slot="${slotName}"]`)) {
    const div = document.createElement('div');
    div.slot = slotName;
    div.id = id;
    div.style['width'] = width;

    root.appendChild(div);
  }
}

export function initShadowMountPoints(host: Element, slotName: string): void {
  // trace each shadow boundary between us and the document
  const shadowHosts = [host];
  while ((host = (host.getRootNode() as ShadowRoot).host)) {
    shadowHosts.push(host);
  }

  const hosts = [...shadowHosts];
  // pop the root
  hosts.pop();

  hosts.forEach(item => {
    const slot = document.createElement('slot');
    slot.slot = slotName;
    slot.name = slotName;

    item.append(slot);
  });
}

export function loadScript(url, callback, type = 'text/javascript') {
  // Adding the script tag to the head as suggested before
  const existingEl = document.head.querySelector(`script[src="${url}"]`);
  if (existingEl) {
    return;
  }

  const script = document.createElement('script');
  script.type = type;
  script.src = url;

  // Then bind the event to the callback function.
  // There are several events for cross browser compatibility.
  // script.onreadystatechange = callback;
  script.onload = callback;

  // Fire the loading
  document.head.appendChild(script);
}

export function addStyle(content: string) {
  const script = document.createElement('style');
  script.innerHTML = content;

  document.head.appendChild(script);
}

export function createCssCustomProperty(name: string, value: string) {
  return `--${name}: ${value};`;
}

export function addLink(href: string, rel: string) {
  const script = document.createElement('link');
  script.href = href;
  script.rel = rel;

  document.head.appendChild(script);
}

export function addRentwareFont() {
  addLink('https://w-cdn.rentware.io/fonts/nunito/nunito_300_400_700.css', 'stylesheet');
}

export function currentUrlWithExtraQueryParams(extraParams: { name: string; value: string }[]): string {
  const queryParams = new URLSearchParams(window.location.search);
  extraParams.forEach(param => {
    queryParams.append(param.name, param.value);
  });

  const finalUrl = new URL(window.location.href);
  finalUrl.search = queryParams.toString();

  return finalUrl.href;
}

// Changes https://example.com/?q=cat#gallery into https://example.com/?q=cat
export function removeHashFromUrl(url: string): string {
  const currentUrl = new URL(url);
  const finalUrl = new URL(currentUrl.origin + currentUrl.pathname + currentUrl.search);

  return finalUrl.href;
}

export function removeRentwareQueryParams(queryParams: URLSearchParams): URLSearchParams {
  const keysToDelete = [];
  queryParams.forEach((_, key) => {
    if (key.startsWith('rentware-')) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => {
    queryParams.delete(key);
  });

  return queryParams;
}

export function removePaymentQueryParams(queryParams: URLSearchParams): URLSearchParams {
  const keysToDelete = ['rentware-payment', 'rentware-order-id'];
  keysToDelete.forEach(key => {
    queryParams.delete(key);
  });

  return queryParams;
}

export function removePayPalQueryParams(queryParams: URLSearchParams): URLSearchParams {
  const keysToDelete = ['token', 'paymentId', 'PayerID'];
  keysToDelete.forEach(key => {
    queryParams.delete(key);
  });

  return queryParams;
}

export function removeStripeQueryParams(queryParams: URLSearchParams): URLSearchParams {
  const keysToDelete = ['payment_intent', 'payment_intent_client_secret', 'redirect_status'];
  keysToDelete.forEach(key => {
    queryParams.delete(key);
  });

  return queryParams;
}

export function historyReplaceStateQueryParams(queryParams: URLSearchParams) {
  let finalUrl = window.location.pathname;
  if (queryParams.toString() !== '') {
    finalUrl += '?' + queryParams.toString();
  }

  try {
    window.history.replaceState(null, '', finalUrl);
  } catch (e) {
    console.error('error in history replace state', e);
  }
}

export function getCountryFromLocale(locale: string): string {
  if (!locale) {
    return '';
  }

  if (locale.includes('-')) {
    const [, countryCode] = locale.split('-');
    return countryCode;
  }

  return locale;
}

export function month(d: Date): string {
  return format(d, 'yyyy-MM');
}

export function localizedMonth(d: Date, locale: Locale): string {
  return format(d, 'LLLL yyyy', { locale: locale });
}

export function localizedDaysOfWeek(locale): string[] {
  return [...Array(7).keys()].map(i => locale.localize.day(i, { width: 'short' }));
}

export function monthsDateRange(month: Date, numberOfMonths: number): DateRangeStr {
  return {
    from: dateToString(startOfMonth(month)),
    to: dateToString(addDays(addMonths(startOfMonth(month), numberOfMonths), -1)),
  };
}

export function wholeMonthDateRange(month: Date): DateRangeStr {
  return {
    from: dateToString(startOfMonth(month)),
    to: dateToString(endOfMonth(month)),
  };
}

export function twoMonthDateRange(month: Date): DateRangeStr {
  return {
    from: dateToString(startOfMonth(add(month, { months: -1 }))),
    to: dateToString(endOfMonth(add(month, { months: 1 }))),
  };
}

const depsMemory: { [id: string]: string[] } = {};

export function callIfDepsChanged(id: string, fn: () => any, deps: string[], force: boolean) {
  const call = () => {
    fn();
    if (deps.length > 0) {
      depsMemory[id] = deps;
    }
  };

  if (force || deps.length == 0) {
    return call();
  }

  if (!depsMemory[id]) {
    return call();
  }

  if (deps.join('-') !== depsMemory[id].join('-')) {
    return call();
  }
}

// Useful for debugging race conditions and loading states
export async function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function hyphensToCamelCase(text: string): string {
  return text.replace(/-([a-z])/g, function (g) {
    return g[1].toUpperCase();
  });
}

export function capitalizeFirstLetter([first, ...rest]: string): string {
  return first.toUpperCase() + rest.join('');
}

export function uncapitalizeFirstLetter([first, ...rest]: string): string {
  return first.toLowerCase() + rest.join('');
}

export function priceWithMileagePerTimeT(price: Price, priceOptions: PriceOptions, amountToBook: number, mileageOptions?: MileageOption[], selectedMileageId?: string): string {
  let mileagePrice = 0;
  const priceAmount = priceOptions.displayNet ? price.net : price.gross;

  if (mileageOptions && !!selectedMileageId) {
    // temp commented
  //   const selectedMileagePrice = mileageOptions.find(item => item.id === selectedMileageId).relativePrice?.price;
  //   mileagePrice = priceOptions.displayNet ? selectedMileagePrice.net : selectedMileagePrice.gross;
  }

  const fullPrice = (priceAmount + mileagePrice) * amountToBook;
  const interval: string = priceOptions.priceCalculation;
  const amount = formatPrice(fullPrice);

  return interval === PriceCalculation.Interval ? amount : t('checkout.pricePer' + capitalizeFirstLetter(interval), { amount: amount }); //format: 500$ per day
}

export function priceWithMileagePerTime(
  price: Price,
  priceOptions: PriceOptions,
  amountToBook: number,
  locale: string,
  translations: Translations,
  mileageOptions?,
  selectedMileageId?: string,
): string {
  let mileagePrice = 0;
  const priceAmount = priceOptions.displayNet ? price.net : price.gross;

  if (mileageOptions && !!selectedMileageId) {
    const selectedMileagePrice = mileageOptions.find(item => item.id === selectedMileageId).price;
    mileagePrice = priceOptions.displayNet ? selectedMileagePrice.net : selectedMileagePrice.gross;
  }

  const fullPrice = (priceAmount + mileagePrice) * amountToBook;
  const interval: string = priceOptions.priceCalculation;
  const amount = formatPrice(fullPrice);

  return interval === PriceCalculation.Interval ? amount : trans('checkout.pricePer' + capitalizeFirstLetter(interval), locale, translations, { amount: amount }); //format: 500$ per day
}

function getButtonShadowStylePrimary(shadowType: ShadowType): string {
  if (shadowType === ShadowType.None) {
    return 'none';
  } else if (shadowType === ShadowType.Soft) {
    return '1px 3px 8px 0';
  } else if (shadowType === ShadowType.Hard) {
    return '2px 6px 16px 0';
  }
}

function getButtonShadowStyleSecondary(shadowType: ShadowType): string {
  if (shadowType === ShadowType.None) {
    return 'none';
  } else if (shadowType === ShadowType.Soft) {
    return '-1px 3px 8px 0';
  } else if (shadowType === ShadowType.Hard) {
    return '-2px 6px 16px 0';
  }
}

function getShadowStylePrimary(shadowType: ShadowType): string {
  if (shadowType === ShadowType.None) {
    return 'none';
  } else if (shadowType === ShadowType.Soft) {
    return '2px 6px 10px 0';
  } else if (shadowType === ShadowType.Hard) {
    return '4px 12px 20px 0';
  }
}

function getShadowStyleSecondary(shadowType: ShadowType): string {
  if (shadowType === ShadowType.None) {
    return 'none';
  } else if (shadowType === ShadowType.Soft) {
    return '-2px 6px 10px 0';
  } else if (shadowType === ShadowType.Hard) {
    return '-4px 12px 20px 0';
  }
}

function getRounding(roundingType: RoundingType) {
  if (roundingType === RoundingType.None) {
    return '0';
  } else if (roundingType === RoundingType.Normal) {
    return '10px';
  } else if (roundingType === RoundingType.Wide) {
    return '20px';
  }
}

export function getButtonRounding(roundingType: RoundingType) {
  if (roundingType === RoundingType.None) {
    return '0';
  } else if (roundingType === RoundingType.Normal) {
    return '5px';
  } else if (roundingType === RoundingType.Wide) {
    return '10px';
  }
}

function getButtonShape(cartButtonShape: CartButtonShape) {
  if (cartButtonShape === CartButtonShape.Circle) {
    return '50%';
  } else if (cartButtonShape === CartButtonShape.Rounded) {
    return '10px';
  } else if (cartButtonShape === CartButtonShape.Square) {
    return '0';
  }
}

function getFontFamily(fontType: SettingsFontType) {
  addRentwareFont();

  if (fontType === SettingsFontType.Rentware) {
    return 'Nunito';
  }
}

function getCartTranslate(cartPosition: CartPosition) {
  if (cartPosition === CartPosition.TopLeft || cartPosition === CartPosition.TopRight) {
    return '-20px';
  } else {
    return '20px';
  }
}

function getCartPosition(cartPosition: CartPosition) {
  const offset = '20px';

  if (cartPosition === CartPosition.BottomLeft) {
    return {
      top: null,
      bottom: offset,
      left: offset,
      right: null,
    };
  } else if (cartPosition === CartPosition.BottomRight) {
    return {
      top: null,
      bottom: offset,
      left: null,
      right: offset,
    };
  } else if (cartPosition === CartPosition.TopLeft) {
    return {
      top: offset,
      bottom: null,
      left: offset,
      right: null,
    };
  } else if (cartPosition === CartPosition.TopRight) {
    return {
      top: offset,
      bottom: null,
      left: null,
      right: offset,
    };
  }
}

export function setNewWidgetCustomStyles(config: WidgetConfig) {
  const globalSettingsFontFamily = getFontFamily(config.advanced.general.font);
  const globalSettingsBackgroundPrimary = new Color(config.advanced.wrapper.colorBackground).RGBString;
  const globalSettingsBackgroundSecondary = new Color(config.advanced.wrapper.colorBackground).calculateSecondaryBackgroundRGB();
  const globalSettingsBackgroundTertiary = new Color(config.advanced.wrapper.colorBackground).calculateTertiaryBackgroundRGB();
  const globalSettingsBackgroundShadow = new Color(config.advanced.wrapper.colorBackground).calculateShadowRGB();
  const globalSearchStyleBackground = new Color(config.customize.searchConfig.styling.colorSearchBackground).RGBString;

  const globalSettingsStrokePrimary = new Color(config.advanced.wrapper.colorBorder).RGBString;
  const globalSettingsStrokeSecondary = new Color(config.advanced.wrapper.colorBorder).calculateSecondaryRGB(new Color(globalSettingsBackgroundPrimary));
  const globalSettingsStrokeTertiary = new Color(config.advanced.wrapper.colorBorder).calculateTertiaryRGB(new Color(globalSettingsBackgroundPrimary));

  const globalSettingsFontPrimary = new Color(config.customize.sharedConfig.colorText).RGBString;
  const globalSettingsFontSecondary = new Color(config.customize.sharedConfig.colorText).calculateSecondaryRGB(new Color(globalSettingsBackgroundPrimary));
  const globalSettingsFontTertiary = new Color(config.customize.sharedConfig.colorText).calculateTertiaryRGB(new Color(globalSettingsBackgroundPrimary));

  const globalErrorBackground = new Color(config.advanced.wrapper.errorColorBackground).RGBString;
  const globalErrorFont = new Color(config.advanced.wrapper.errorColorText).RGBString;

  const globalSettingRounding = getRounding(config.advanced.wrapper.rounding);
  const globalButtonRounding = getButtonRounding(config.customize.sharedConfig.button.rounding);
  const globalSettingShadowPrimary = getShadowStylePrimary(config.advanced.wrapper.shadow);
  const globalSettingShadowSecondary = getShadowStyleSecondary(config.advanced.wrapper.shadow);
  const globalButtonShadowPrimary = getButtonShadowStylePrimary(config.advanced.wrapper.shadow);
  const globalButtonShadowSecondary = getButtonShadowStyleSecondary(config.advanced.wrapper.shadow);

  const globalButtonBackgroundPrimary = new Color(config.customize.sharedConfig.button.colorBackground).RGBString;
  const globalButtonBackgroundSecondary = new Color(config.customize.sharedConfig.button.colorBackground).calculateSecondaryRGB(new Color(globalSettingsBackgroundPrimary));
  const globalButtonBackgroundTertiary = new Color(config.customize.sharedConfig.button.colorBackground).calculateTertiaryRGB(new Color(globalSettingsBackgroundPrimary));

  const globalButtonFontPrimary = new Color(config.customize.sharedConfig.button.colorText).RGBString;
  const globalButtonFontSecondary = new Color(config.customize.sharedConfig.button.colorText).calculateSecondaryRGB(new Color(globalButtonBackgroundPrimary));
  const globalButtonFontTertiary = new Color(config.customize.sharedConfig.button.colorText).calculateTertiaryRGB(new Color(globalButtonBackgroundPrimary));

  const globalCalendarSelectionBackgroundPrimary = new Color(config.advanced.calendar.colorSelectionBackground).RGBString;
  const globalCalendarSelectionBackgroundSecondary = new Color(config.advanced.calendar.colorSelectionBackground).calculateSecondaryBackgroundRGB();
  const globalCalendarSelectionBackgroundQuaternary = new Color(config.advanced.calendar.colorSelectionBackground).calculateQuaternaryBackgroundRGB();
  const globalCalendarSelectionFont = new Color(config.advanced.calendar.colorSelectionText).RGBString;
  const globalCalendarAvailable = new Color(config.advanced.calendar.colorAvailable).RGBString;

  const cartStyleButtonShadowPrimary = getShadowStylePrimary(config.advanced.cart.cartButtonShadow);
  const cartStyleButtonShadowSecondary = getShadowStyleSecondary(config.advanced.cart.cartButtonShadow);
  const cartStyleButtonShape = getButtonShape(config.advanced.cart.cartButtonShape);

  const globalCartViewPositionLeft = getCartPosition(config.advanced.cart.cartPosition).left;
  const globalCartViewPositionRight = getCartPosition(config.advanced.cart.cartPosition).right;
  const globalCartViewPositionTop = getCartPosition(config.advanced.cart.cartPosition).top;
  const globalCartViewPositionBottom = getCartPosition(config.advanced.cart.cartPosition).bottom;
  const globalCartViewTranslate = getCartTranslate(config.advanced.cart.cartPosition);

  const globalCartColorsButtonBackgroundPrimary = new Color(config.advanced.cart.colorCartButtonBackground).RGBString;
  const globalCartColorsButtonBackgroundSecondary = new Color(config.advanced.cart.colorCartButtonBackground).calculateSecondaryBackgroundRGB();
  const globalCartColorsButtonBackgroundTertiary = new Color(config.advanced.cart.colorCartButtonBackground).calculateTertiaryBackgroundRGB();
  const globalCartColorsButtonIcon = new Color(config.advanced.cart.colorCartButtonIcon).RGBString;

  const globalCartColorsAmountBackgroundPrimary = new Color(config.advanced.cart.colorCartAmountBackground).RGBString;
  const globalCartColorsAmountBackgroundSecondary = new Color(config.advanced.cart.colorCartAmountBackground).calculateSecondaryBackgroundRGB();
  const globalCartColorsAmountFont = new Color(config.advanced.cart.colorCartAmountFont).RGBString;

  const globalCartColorsActiveTab = new Color(config.advanced.checkout.colorActiveTab).RGBString;
  const globalCartColorsStatusBar = new Color(config.advanced.checkout.colorStatusBarBackground).RGBString;

  let styleText = ':root {';
  if (globalSettingsFontFamily) {
    styleText += `${createCssCustomProperty('rentware-global-settings-font-family', globalSettingsFontFamily)}`;
  }

  styleText += `
  ${createCssCustomProperty('rentware-global-settings-background-primary', globalSettingsBackgroundPrimary)}
  ${createCssCustomProperty('rentware-global-settings-background-secondary', globalSettingsBackgroundSecondary)}
  ${createCssCustomProperty('rentware-global-settings-background-tertiary', globalSettingsBackgroundTertiary)}
  ${createCssCustomProperty('rentware-global-settings-background-shadow', globalSettingsBackgroundShadow)}
  ${createCssCustomProperty('rentware-global-settings-stroke-primary', globalSettingsStrokePrimary)}
  ${createCssCustomProperty('rentware-global-settings-stroke-secondary', globalSettingsStrokeSecondary)}
  ${createCssCustomProperty('rentware-global-settings-stroke-tertiary', globalSettingsStrokeTertiary)}
  ${createCssCustomProperty('rentware-global-settings-font-primary', globalSettingsFontPrimary)}
  ${createCssCustomProperty('rentware-global-settings-font-secondary', globalSettingsFontSecondary)}
  ${createCssCustomProperty('rentware-global-settings-font-tertiary', globalSettingsFontTertiary)}
  ${createCssCustomProperty('rentware-global-settings-rounding', globalSettingRounding)}
  ${createCssCustomProperty('rentware-global-settings-shadow-primary', globalSettingShadowPrimary)}
  ${createCssCustomProperty('rentware-global-settings-shadow-secondary', globalSettingShadowSecondary)}
  ${createCssCustomProperty('rentware-global-button-rounding', globalButtonRounding)}
  ${createCssCustomProperty('rentware-global-button-shadow-primary', globalButtonShadowPrimary)}
  ${createCssCustomProperty('rentware-global-button-shadow-secondary', globalButtonShadowSecondary)}
  ${createCssCustomProperty('rentware-global-button-background-primary', globalButtonBackgroundPrimary)}
  ${createCssCustomProperty('rentware-global-button-background-secondary', globalButtonBackgroundSecondary)}
  ${createCssCustomProperty('rentware-global-button-background-tertiary', globalButtonBackgroundTertiary)}
  ${createCssCustomProperty('rentware-global-button-font-primary', globalButtonFontPrimary)}
  ${createCssCustomProperty('rentware-global-button-font-secondary', globalButtonFontSecondary)}
  ${createCssCustomProperty('rentware-global-button-font-tertiary', globalButtonFontTertiary)}
  ${createCssCustomProperty('rentware-global-calendar-selection-background-primary', globalCalendarSelectionBackgroundPrimary)}
  ${createCssCustomProperty('rentware-global-calendar-selection-background-secondary', globalCalendarSelectionBackgroundSecondary)}
  ${createCssCustomProperty('rentware-global-calendar-selection-background-quaternary', globalCalendarSelectionBackgroundQuaternary)}
  ${createCssCustomProperty('rentware-global-calendar-selection-font', globalCalendarSelectionFont)}
  ${createCssCustomProperty('rentware-global-calendar-available', globalCalendarAvailable)}
  ${createCssCustomProperty('rentware-global-error-background', globalErrorBackground)}
  ${createCssCustomProperty('rentware-global-error-font', globalErrorFont)}
  ${createCssCustomProperty('rentware-cart-style-button-shadow-primary', cartStyleButtonShadowPrimary)}
  ${createCssCustomProperty('rentware-cart-style-button-shadow-secondary', cartStyleButtonShadowSecondary)}
  ${createCssCustomProperty('rentware-cart-style-button-shape', cartStyleButtonShape)}
  ${createCssCustomProperty('rentware-cart-colors-button-background-primary', globalCartColorsButtonBackgroundPrimary)}
  ${createCssCustomProperty('rentware-cart-colors-button-background-secondary', globalCartColorsButtonBackgroundSecondary)}
  ${createCssCustomProperty('rentware-cart-colors-button-background-tertiary', globalCartColorsButtonBackgroundTertiary)}
  ${createCssCustomProperty('rentware-cart-colors-button-icon', globalCartColorsButtonIcon)}
  ${createCssCustomProperty('rentware-cart-colors-amount-background-primary', globalCartColorsAmountBackgroundPrimary)}
  ${createCssCustomProperty('rentware-cart-colors-amount-background-secondary', globalCartColorsAmountBackgroundSecondary)}
  ${createCssCustomProperty('rentware-cart-colors-amount-font', globalCartColorsAmountFont)}
  ${createCssCustomProperty('rentware-cart-colors-active-tab', globalCartColorsActiveTab)}
  ${createCssCustomProperty('rentware-cart-colors-status-bar', globalCartColorsStatusBar)}
  ${createCssCustomProperty('rentware-cart-view-position-left', globalCartViewPositionLeft)}
  ${createCssCustomProperty('rentware-cart-view-position-right', globalCartViewPositionRight)}
  ${createCssCustomProperty('rentware-cart-view-position-top', globalCartViewPositionTop)}
  ${createCssCustomProperty('rentware-cart-view-position-bottom', globalCartViewPositionBottom)}
  ${createCssCustomProperty('rentware-cart-view-translate', globalCartViewTranslate)}
  ${createCssCustomProperty('rentware-search-style-background', globalSearchStyleBackground)}

}`;

  addStyle(styleText);
}

export const getElementAttributes = (nodeMap: NamedNodeMap, exclude: { [key: string]: boolean }) => {
  if (nodeMap) {
    const length = nodeMap.length;
    return Object.keys(nodeMap).reduce((props, current) => {
      try {
        const numCurrent = parseInt(current);
        if (numCurrent < length) {
          const property = nodeMap[current];
          if (!property || exclude[property.name]) {
            return props;
          }

          return {
            ...props,
            [property.name]: property.value,
          };
        }
      } catch (err) {}
    }, {});
  }

  return {};
};

export function getCountriesOptions(locale: string, translations: any) {
  const localeToLanguage = (locale: string): string => {
    // exceptions
    if (locale === 'en-GB') {
      return 'gb';
    } else if (locale === 'en-US') {
      return 'us';
    }

    if (!locale.includes('-')) {
      return locale;
    }

    const [lang] = locale.split('-');

    return lang;
  };

  const countriesObject: CountryOption[] = countriesList(locale, translations);
  const countriesArray = Object.entries(countriesObject);
  const systemLocale: string = localeToLanguage(locale).toUpperCase();

  const indexOfMainCountry: number = countriesArray.findIndex(localeArr => localeArr[0] === systemLocale);

  // Change place of system language to the top
  if (indexOfMainCountry > -1) {
    countriesArray.unshift(countriesArray.splice(indexOfMainCountry, 1)[0]);
  }

  return countriesArray.map(countryArray => {
    return {
      value: countryArray[0],
      label: countryArray[1],
    };
  });
}

export function buildPickUpDateTimeForApi(details: BookingDetails, withTime: boolean) {
  const pickUpTime = withTime && !!details.fromTime ? details.fromTime + ':00' : '12:00:00';

  return details.fromDate ? dateToString(details.fromDate) + 'T' + pickUpTime : null;
}

export function buildDropOffDateTimeForApi(details: BookingDetails, withTime: boolean) {
  const dropOffTime = withTime && !!details.toTime ? details.toTime + ':00' : '12:00:00';

  return details.fromDate ? dateToString(details.toDate || details.fromDate) + 'T' + dropOffTime : null;
}

export function debounce(func, delay) {
  let timeoutId;

  return function(...args) {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}
